import * as React from 'react';

import {
  CurrencyCode,
  enumPurchaseLoadStatus,
  enumUnit,
  PurchaseLoadStatus,
} from '@rematter/frontend-lib-api-client';
import { Button, Col, ConsoleTouchDialog } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideRefreshCw } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { ItemsInterface } from '../../_shared/ItemsInterface';
import {
  enumLineItemKind,
  ItemsListEntry,
  NormalizedLineItem,
} from '../../_shared/ItemsInterface/_types';
import { useAutoPrice } from '../hooks/useAutoPrice';
import { QuickAddMaterialButtons } from '../shared/QuickAddMaterialButtons';

import {
  $materialsLocalEntriesById,
  $setMaterialsDirty,
  $setMaterialsLocalEntries,
} from '~/modules/trade/_state/materialsForm.state';
import { $setAutoFocusRowId } from '~/modules/trade/_state/materialsInterface.state';
import {
  $inboundDeductionChange,
  $inboundWeightUnitMode,
  $saveInboundMaterials,
  normalizeInboundEntries,
} from '~/modules/trade/_state/scaleTicketInboundMaterialsForm.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { PurchaseLoadRecord } from '~/modules/trade/types/purchaseLoad';
import { zeroWeight } from '~/modules/trade/utils/weight';
import { useTheme, useUIPreferences } from '~/shared/hooks';

type InboundMaterialsEditorProps = {
  ticket: PurchaseLoadRecord;
  disabled?: boolean;
};

const INVENTORY_LOCKED_STATUSES: PurchaseLoadStatus[] = [
  enumPurchaseLoadStatus.InventoryComplete,
  enumPurchaseLoadStatus.PriceComplete,
  enumPurchaseLoadStatus.Paid,
];

//
// ⚛️ ⚛️ ⚛️ INBOUND MATERIALS EDITOR ⚛️ ⚛️ ⚛️
//
export const InboundMaterialsEditor = ({
  ticket,
  disabled = false,
}: InboundMaterialsEditorProps) => {
  const t = useTranslations();
  const { effectiveColorScheme } = useTheme();
  const { touchModeEnabled } = useUIPreferences();

  const weightUnitMode = useAtomValue($inboundWeightUnitMode);
  const atomEntries = useAtomValue($materialsLocalEntriesById).get(ticket.id);
  const fallbackEntries = React.useMemo(
    () =>
      normalizeInboundEntries(ticket.lineItems ?? [], ticket.deductions ?? []),
    [ticket.lineItems, ticket.deductions],
  );
  const localEntries = atomEntries ?? fallbackEntries;
  const setLocalEntries = useSetAtom($setMaterialsLocalEntries);
  const setDirty = useSetAtom($setMaterialsDirty);
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const saveInboundMaterials = useSetAtom($saveInboundMaterials);
  const dispatchDeductionChange = useSetAtom($inboundDeductionChange);

  const inventoryLocked = INVENTORY_LOCKED_STATUSES.includes(ticket.status);

  const isAutoMergingRef = React.useRef(false);
  const hasAutoInsertedRef = React.useRef(false);
  const localEntriesRef = React.useRef(localEntries);
  localEntriesRef.current = localEntries;

  const localLineItems = React.useMemo(
    () =>
      localEntries.filter(
        (e): e is NormalizedLineItem => e.kind === enumLineItemKind.LineItem,
      ),
    [localEntries],
  );

  const {
    resolvedPrices,
    markUserOverride,
    pendingContextChange,
    confirmContextChange,
  } = useAutoPrice({
    facilityId: ticket.facilityId,
    supplierAccountId: ticket.supplierAccountId,
    currencyCode: ticket.currencyCode as CurrencyCode,
    lineItems: localLineItems,
    enabled: !inventoryLocked,
  });

  React.useEffect(() => {
    if (resolvedPrices.size === 0) return;

    const snapshotEntries = localEntriesRef.current;
    let hasChanges = false;
    const updated = snapshotEntries.map((entry) => {
      if (entry.kind !== enumLineItemKind.LineItem) return entry;
      const item = entry as NormalizedLineItem;
      if (!resolvedPrices.has(item.id)) return entry;
      const resolved = resolvedPrices.get(item.id) ?? null;
      if (resolved === null) {
        if (item.unitPrice === null) return entry;
        hasChanges = true;
        return { ...item, unitPrice: null };
      }
      if (
        item.unitPrice?.amount === resolved.amount &&
        item.unitPrice?.currencyCode === resolved.currencyCode
      ) {
        return entry;
      }
      hasChanges = true;
      return { ...item, unitPrice: resolved };
    });

    if (!hasChanges) return;
    if (localEntriesRef.current !== snapshotEntries) return;

    isAutoMergingRef.current = true;
    setLocalEntries({ ticketId: ticket.id, entries: updated });
    setDirty({ ticketId: ticket.id, dirty: true });
    saveInboundMaterials({
      ticketId: ticket.id,
      failedToSaveMessage: t.scaleTicketPro.materialsTab.failedToSaveMaterials,
    });
    requestAnimationFrame(() => {
      isAutoMergingRef.current = false;
    });
  }, [
    resolvedPrices,
    ticket.id,
    setLocalEntries,
    setDirty,
    saveInboundMaterials,
    t,
  ]);

  const handleUpdateEntries = React.useCallback(
    (updatedEntries: ItemsListEntry[]) => {
      if (!isAutoMergingRef.current) {
        for (const entry of updatedEntries) {
          if (entry.kind !== enumLineItemKind.LineItem) continue;
          const updatedItem = entry as NormalizedLineItem;
          const prevEntry = localEntriesRef.current.find(
            (e) =>
              e.kind === enumLineItemKind.LineItem && e.id === updatedItem.id,
          ) as NormalizedLineItem | undefined;
          if (
            prevEntry &&
            updatedItem.unitPrice !== null &&
            prevEntry.unitPrice?.amount !== updatedItem.unitPrice?.amount
          ) {
            markUserOverride(updatedItem.id);
          }
        }
      }
      setLocalEntries({ ticketId: ticket.id, entries: updatedEntries });
      setDirty({ ticketId: ticket.id, dirty: true });
      saveInboundMaterials({
        ticketId: ticket.id,
        failedToSaveMessage:
          t.scaleTicketPro.materialsTab.failedToSaveMaterials,
      });
    },
    [
      setLocalEntries,
      setDirty,
      saveInboundMaterials,
      ticket.id,
      t,
      markUserOverride,
    ],
  );

  const handleAddItemFromQuickAdd = React.useCallback(
    (type: 'Raw' | 'Tagged', data?: Partial<NormalizedLineItem>) => {
      const newItem: NormalizedLineItem = {
        kind: enumLineItemKind.LineItem,
        id: data?.id ?? `temp-${uuidv4()}`,
        type,
        itemId: data?.itemId ?? null,
        tagId: data?.tagId ?? null,
        weights: data?.weights ?? {
          gross: zeroWeight(),
          tare: zeroWeight(),
          net: zeroWeight(),
        },
        unitPrice: data?.unitPrice ?? null,
        pricingUnit: data?.pricingUnit ?? enumUnit.Pounds,
        notes: data?.notes,
        sourceLineId: data?.sourceLineId,
      };
      const updated = [...localEntries, newItem];
      setLocalEntries({ ticketId: ticket.id, entries: updated });
      setDirty({ ticketId: ticket.id, dirty: true });
      saveInboundMaterials({
        ticketId: ticket.id,
        failedToSaveMessage:
          t.scaleTicketPro.materialsTab.failedToSaveMaterials,
      });
    },
    [
      localEntries,
      setLocalEntries,
      setDirty,
      saveInboundMaterials,
      ticket.id,
      t,
    ],
  );

  React.useEffect(() => {
    if (
      hasAutoInsertedRef.current ||
      localEntries.length > 0 ||
      ticket.status !== enumPurchaseLoadStatus.Open ||
      disabled
    ) {
      return;
    }
    hasAutoInsertedRef.current = true;
    const newId = `temp-${uuidv4()}`;
    handleAddItemFromQuickAdd('Raw', { id: newId });
    setAutoFocusRowId(newId);
  }, [
    localEntries.length,
    ticket.status,
    disabled,
    handleAddItemFromQuickAdd,
    setAutoFocusRowId,
  ]);

  const handleDeductionChange = React.useCallback(
    (...args: Parameters<typeof dispatchDeductionChange>) => {
      dispatchDeductionChange(...args);
    },
    [dispatchDeductionChange],
  );

  const sidebarContent = React.useMemo(
    () =>
      !inventoryLocked ? (
        <QuickAddMaterialButtons
          onAddItem={handleAddItemFromQuickAdd}
          onFocusNewItem={setAutoFocusRowId}
          maxItems={5}
        />
      ) : null,
    [inventoryLocked, handleAddItemFromQuickAdd, setAutoFocusRowId],
  );

  return (
    <Col className="@InboundMaterialsEditor h-full" gap1>
      <div className="min-h-0 flex-1">
        <ItemsInterface
          showPricing
          entries={localEntries}
          onUpdateEntries={handleUpdateEntries}
          disabled={disabled}
          inventoryLocked={inventoryLocked}
          facilityDefaultUnit={enumUnit.Pounds}
          weightUnitMode={weightUnitMode === 'scale' ? 'scale' : 'price'}
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          heavyWeight={ticket.heavyWeight}
          lightWeight={ticket.lightWeight}
          sidebarContent={sidebarContent}
          onDeductionChange={handleDeductionChange}
        />
      </div>

      <ConsoleTouchDialog
        touchModeEnabled={touchModeEnabled}
        effectiveColorScheme={effectiveColorScheme}
        isOpen={pendingContextChange !== null}
        onOpenChange={(open) => {
          if (!open) confirmContextChange(false);
        }}
        className="@RepricingConfirmDialog"
        dialogClassName="max-h-[80vh] w-[90vw] max-w-[440px]"
        header={
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <LucideRefreshCw className="h-5 w-5 text-primary" />
            </div>
            <div className="text-base font-semibold">
              {t.scaleTicketPro.materialsTab.repricingDialog.title}
            </div>
          </div>
        }
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              variant="text"
              color="neutral"
              onPress={() => confirmContextChange(false)}
            >
              {t.scaleTicketPro.materialsTab.repricingDialog.keepManualPrices}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={() => confirmContextChange(true)}
            >
              {t.scaleTicketPro.materialsTab.repricingDialog.reResolveAll}
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <div className="text-base text-neutral/50">
            {t.scaleTicketPro.materialsTab.repricingDialog.description}{' '}
            {pendingContextChange?.overriddenCount === 1
              ? t.scaleTicketPro.materialsTab.repricingDialog
                  .singleItemOverridden
              : t.scaleTicketPro.materialsTab.repricingDialog.multipleItemsOverridden(
                  pendingContextChange?.overriddenCount ?? 0,
                )}{' '}
            {t.scaleTicketPro.materialsTab.repricingDialog.prompt}
          </div>
        </div>
      </ConsoleTouchDialog>
    </Col>
  );
};
